import { Loader2Icon } from 'lucide-react';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import type { DocumentModelKindDTO, DocumentThemePresetDTO } from '@shared/schemas/document-model';
import {
  ArtifactClose,
  ArtifactDescription,
  ArtifactHeader,
  ArtifactTitle,
} from '@/components/ai-elements/artifact';
import { ChatDocumentKindIcon } from '../components/ChatDocumentKindIcon';
import { ChatDocumentVersionsMenu } from '../components/ChatDocumentVersionsMenu';
import { CanvasDownloadMenu } from './CanvasDownloadMenu';
import { CanvasThemeMenu } from './CanvasThemeMenu';

type CanvasHeaderProps = {
  /** Version (génération) actuellement affichée. */
  currentGenerationId: string;
  conversationId: string;
  document: ChatDocumentDTO;
  /** Identifiant du document généré (famille de versions), connu une fois une version terminée. */
  familyId: string | undefined;
  isSaving: boolean;
  isSucceeded: boolean;
  kind: DocumentModelKindDTO | null;
  onClose: () => void;
  onOpenVersion: (version: { generationId: string; revisionNumber: number }) => void;
  onThemeChange: (preset: DocumentThemePresetDTO) => void;
  statusLabel: string | null;
  themePreset: DocumentThemePresetDTO | null;
  title: string;
  versionNumber: number | undefined;
};

export function CanvasHeader({
  conversationId,
  currentGenerationId,
  document,
  familyId,
  isSaving,
  isSucceeded,
  kind,
  onClose,
  onOpenVersion,
  onThemeChange,
  statusLabel,
  themePreset,
  title,
  versionNumber,
}: CanvasHeaderProps) {
  const subtitle = [
    versionNumber ? `Version ${versionNumber}` : null,
    statusLabel,
  ].filter(Boolean).join(' · ');

  return (
    <ArtifactHeader className="@container min-h-[3.25rem] gap-2 border-border/50 bg-muted/20 px-3 @lg:px-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <ChatDocumentKindIcon document={document} size="xs" />
        <div className="min-w-0">
          <ArtifactTitle className="truncate text-sm font-semibold">{title}</ArtifactTitle>
          <ArtifactDescription className="flex items-center gap-1.5 truncate text-xs">
            {isSaving ? <Loader2Icon className="size-3 animate-spin" aria-hidden /> : null}
            <span aria-live="polite" className="truncate">
              {isSaving ? 'Enregistrement…' : (subtitle || 'Document généré')}
            </span>
          </ArtifactDescription>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-0.5 @lg:gap-1">
        {familyId ? (
          <ChatDocumentVersionsMenu
            conversationId={conversationId}
            currentGenerationId={currentGenerationId}
            generatedDocumentId={familyId}
            labelClassName="hidden @2xl:inline"
            onOpenBlocksVersion={onOpenVersion}
            title={title}
            triggerClassName="h-8 px-2.5 @lg:px-3"
            triggerVariant="ghost"
          />
        ) : null}
        <CanvasThemeMenu
          currentPreset={themePreset}
          disabled={!isSucceeded || isSaving || !familyId}
          onChange={onThemeChange}
        />
        <CanvasDownloadMenu
          conversationId={conversationId}
          disabled={!isSucceeded}
          generationId={currentGenerationId}
          kind={kind}
        />
        <ArtifactClose
          aria-label="Fermer le panneau"
          className="size-8 p-0 text-muted-foreground hover:text-foreground"
          onClick={onClose}
        />
      </div>
    </ArtifactHeader>
  );
}
